import { useEffect, useMemo, useState } from 'react';
import { Search, Coins, Loader2, Package, X } from 'lucide-react';
import {
  getShopCategories, getShopProducts, buyProduct, shopImg,
  type ShopCategory, type ShopProduct, type ShopProductOption,
} from '@/shared/services/shopService';
import { useToast } from '@/shared/contexts/ToastContext';
import { useAuth } from '@/shared/contexts/AuthContext';

/**
 * Cash Shop do site — venda de itens por cash (entrega via up_AddMyCashItemByItem).
 * Os produtos/categorias são cadastrados no painel GM (menu Cash Shop); quando a
 * loja está vazia o menu nem aparece na sidenav (ver DashboardLayout).
 */
export default function CashShopPage() {
  const { notify } = useToast();
  const { refreshCash } = useAuth();
  const [cats, setCats] = useState<ShopCategory[]>([]);
  const [catId, setCatId] = useState<number | null>(null);
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingProds, setLoadingProds] = useState(false);
  const [q, setQ] = useState('');

  const [selected, setSelected] = useState<ShopProduct | null>(null);
  const [optId, setOptId] = useState(0);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    getShopCategories()
      .then((r) => {
        const list = Array.isArray(r) ? r : [];
        setCats(list);
        if (list.length) setCatId(list[0].id);
      })
      .catch(() => setCats([]))
      .finally(() => setLoadingCats(false));
  }, []);

  useEffect(() => {
    if (catId == null) return;
    setLoadingProds(true);
    getShopProducts(catId)
      .then((r) => setProducts(Array.isArray(r) ? r : []))
      .catch(() => setProducts([]))
      .finally(() => setLoadingProds(false));
  }, [catId]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return products;
    return products.filter((p) => (p.title || '').toLowerCase().includes(t) || (p.description || '').toLowerCase().includes(t));
  }, [products, q]);

  // preço = opção selecionada (se houver), senão a primeira opção, senão price base
  const optById = (p: ShopProduct, id: number): ShopProductOption | undefined =>
    p.options?.find((o) => o.id === id);
  const basePrice = (p: ShopProduct) => p.options?.[0]?.price ?? p.price ?? 0;
  const prodPrice = (p: ShopProduct, id?: number) => {
    if (id != null) { const o = optById(p, id); if (o?.price != null) return o.price; }
    return basePrice(p);
  };

  const openBuy = (p: ShopProduct) => { setSelected(p); setOptId(p.options?.[0]?.id ?? 0); setQty(1); };
  const closeBuy = () => setSelected(null);

  const confirmBuy = async () => {
    if (!selected) return;
    setBuying(true);
    try {
      const res = await buyProduct({
        itemid: selected.id, // backend busca pelo Id do produto
        optionid: optId || (selected.options?.[0]?.id ?? 0),
        itemqty: Math.max(1, qty),
      });
      notify('success', res.message || 'Compra realizada!');
      refreshCash?.();
      closeBuy();
    } catch (e: any) {
      notify('error', e?.message || 'Não foi possível comprar.');
    } finally {
      setBuying(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Filtros: categoria + busca */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={catId ?? ''}
          onChange={(e) => setCatId(Number(e.target.value))}
          disabled={loadingCats}
          className="w-full sm:w-64 px-4 py-2.5 text-sm rounded-lg border border-white/10 bg-[#0d141b] text-white outline-none focus:border-[#8fb9de]/60 cursor-pointer"
        >
          {loadingCats ? <option>Carregando…</option> : cats.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Procurar por…"
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-lg border border-white/10 bg-[#0d141b] text-white outline-none focus:border-[#8fb9de]/60 placeholder:text-white/35"
          />
        </div>
      </div>

      {loadingProds ? (
        <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-[#8fb9de]" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-white/40 py-16">Nenhum item encontrado.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const img = shopImg(p.image);
            return (
              <button
                key={p.id}
                onClick={() => openBuy(p)}
                className="group rounded-xl border border-white/[0.08] bg-white/[0.02] flex flex-col text-left hover:border-[#8fb9de]/40 transition cursor-pointer overflow-hidden"
              >
                <div className="px-4 pt-4">
                  <div className="text-sm font-bold text-white leading-tight line-clamp-2 min-h-[2.5rem]">{p.title}</div>
                  {p.description && <div className="text-[11px] text-white/45 mt-1 truncate">{p.description}</div>}
                </div>
                <div className="flex-1 flex items-center justify-center py-6 min-h-[200px]">
                  {img ? (
                    <img src={img} alt="" className="max-h-44 w-auto object-contain drop-shadow-[0_8px_20px_rgba(0,0,0,0.55)] group-hover:scale-105 transition-transform" onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')} />
                  ) : (
                    <Package size={48} className="text-white/15" />
                  )}
                </div>
                <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3">
                  {(p.options?.length ?? 0) > 1 && (
                    <span className="text-[10px] text-white/45 uppercase tracking-wide">{p.options!.length} opções</span>
                  )}
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-black/40 border border-white/10 px-3 py-1 ml-auto">
                    <Coins size={13} className="text-[#8fb9de]" />
                    <span className="text-sm font-bold text-white tabular-nums">{basePrice(p).toLocaleString('pt-BR')}</span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Modal de compra (opções + quantidade) */}
      {selected && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeBuy} />
          <div className="relative w-full max-w-sm rounded-xl border border-white/10 bg-[#0d141b] shadow-2xl max-h-[90vh] overflow-y-auto">
            <button onClick={closeBuy} className="absolute top-3 right-3 text-white/50 hover:text-white cursor-pointer z-10"><X size={18} /></button>
            <div className="p-5 border-b border-white/[0.06]">
              <div className="text-base font-bold text-white">{selected.title}</div>
              {selected.description && <div className="text-xs text-white/55 mt-0.5">{selected.description}</div>}
              <div className="h-40 flex items-center justify-center rounded-lg bg-black/40 border border-white/[0.06] mt-4">
                {shopImg(selected.image)
                  ? <img src={shopImg(selected.image)} alt="" className="max-h-36 object-contain" onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')} />
                  : <Package size={48} className="text-white/15" />}
              </div>
            </div>
            <div className="p-5 space-y-4">
              {/* Seletor de opções (quando há mais de uma) */}
              {(selected.options?.length ?? 0) > 1 && (
                <div>
                  <label className="block text-xs text-white/60 mb-1.5">Opção</label>
                  <div className="flex flex-col gap-1.5 max-h-44 overflow-y-auto pr-1">
                    {selected.options!.map((o) => {
                      const active = optId === o.id;
                      return (
                        <button
                          key={o.id}
                          onClick={() => setOptId(o.id)}
                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left transition cursor-pointer ${
                            active ? 'border-[#8fb9de]/60 bg-[#8fb9de]/10' : 'border-white/10 bg-black/30 hover:border-white/25'
                          }`}
                        >
                          <span className={`text-xs ${active ? 'text-white font-semibold' : 'text-white/70'}`}>{o.title || `Opção ${o.id}`}</span>
                          <span className="inline-flex items-center gap-1 shrink-0">
                            <Coins size={12} className="text-[#8fb9de]" />
                            <span className="text-xs font-bold text-white tabular-nums">{(o.price ?? 0).toLocaleString('pt-BR')}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs text-white/60 mb-1.5">Quantidade</label>
                <input
                  type="number" min={1} max={99} value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-white/10 bg-black/45 text-white outline-none focus:border-[#8fb9de]/60"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/60">Total</span>
                <span className="inline-flex items-center gap-1.5">
                  <Coins size={14} className="text-[#8fb9de]" />
                  <span className="text-base font-extrabold text-white tabular-nums">{(prodPrice(selected, optId) * qty).toLocaleString('pt-BR')}</span>
                </span>
              </div>
              <button
                onClick={confirmBuy}
                disabled={buying}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#8fb9de] hover:bg-[#00cbc2] text-[#04211f] text-sm font-bold px-5 py-2.5 transition disabled:opacity-50 cursor-pointer"
              >
                {buying ? <Loader2 size={15} className="animate-spin" /> : 'Comprar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
